import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { Plus, Trash2, ChevronDown, ChevronUp, Sparkles, Loader2, GripVertical, Info, Zap } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ACTIVITY_TYPES = [
  "Academic", "Art", "Athletics: Club", "Athletics: JV/Varsity",
  "Career Oriented", "Community Service (Volunteer)", "Computer/Technology",
  "Cultural", "Dance", "Debate/Speech", "Environmental", "Family Responsibilities",
  "Foreign Exchange", "Internship", "Journalism/Publication", "Junior R.O.T.C.",
  "Music: Instrumental", "Music: Vocal", "Research", "Robotics",
  "School Spirit", "Science/Math", "Student Govt./Politics",
  "Theater/Drama", "Work (Paid)", "Other Club/Activity"
];

const TIMING_OPTIONS = ["School Year", "School Break", "All Year"];
const GRADE_OPTIONS = [9, 10, 11, 12];
const MAX_ACTIVITIES = 10;
const DESC_LIMIT = 150;

function SortableSlot({ activity, index, onSave, onDelete, onOptimize, optimizing }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: activity?.id || `empty-${index}`, disabled: !activity?.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const [expanded, setExpanded] = useState(!activity);
  const [form, setForm] = useState(activity || {
    name: '', activity_type: '', position_title: '', organization_name: '',
    description: '', grade_levels: [], timing: '', hours_per_week: '',
    weeks_per_year: '', continue_in_college: false,
  });
  const [dirty, setDirty] = useState(false);

  const update = (key, val) => { setForm(prev => ({ ...prev, [key]: val })); setDirty(true); };
  const toggleGrade = (g) => {
    const current = form.grade_levels || [];
    update('grade_levels', current.includes(g) ? current.filter(x => x !== g) : [...current, g].sort());
  };

  const handleSave = () => {
    if (!form.name?.trim()) { toast.error('Activity name is required'); return; }
    onSave(index, { ...form, slot_number: index + 1 });
    setDirty(false);
  };

  const descLen = (form.description || '').length;
  const isComplete = form.name && form.activity_type && form.description;

  return (
    <div ref={setNodeRef} style={style} data-testid={`activity-slot-${index}`}>
      <Card className={`border transition-all ${expanded ? 'border-[#0F392B]/30 shadow-sm' : 'border-[#E2E2E2]'} ${isDragging ? 'shadow-lg' : ''}`}>
        <div className="flex items-center gap-2 px-5 py-3.5">
          {/* Drag handle */}
          {activity?.id && (
            <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 -ml-2 text-muted-foreground/40 hover:text-muted-foreground"
              data-testid={`drag-handle-${index}`}>
              <GripVertical className="h-4 w-4" />
            </button>
          )}
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#0F392B]/5 text-[#0F392B] text-xs font-bold flex-shrink-0">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)} data-testid={`activity-toggle-${index}`}>
            {activity?.name ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{activity.name}</span>
                {activity.position_title && <span className="text-xs text-[#D4AF37]">{activity.position_title}</span>}
                {isComplete && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Complete</Badge>}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground italic">Empty slot</span>
            )}
            {activity?.activity_type && !expanded && <p className="text-xs text-muted-foreground mt-0.5">{activity.activity_type}</p>}
          </div>
          {dirty && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Unsaved</Badge>}
          <button onClick={() => setExpanded(!expanded)} className="p-1">
            {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </button>
        </div>

        {expanded && (
          <CardContent className="px-5 pb-5 pt-0 space-y-4 border-t border-[#E2E2E2]">
            <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Activity Type *</Label>
                <Select value={form.activity_type || ''} onValueChange={v => update('activity_type', v)}>
                  <SelectTrigger data-testid={`activity-type-${index}`} className="border-[#E2E2E2]"><SelectValue placeholder="Select type..." /></SelectTrigger>
                  <SelectContent>{ACTIVITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Position / Leadership</Label>
                <Input value={form.position_title || ''} onChange={e => update('position_title', e.target.value)}
                  placeholder="e.g., President, Captain" className="border-[#E2E2E2]" data-testid={`activity-position-${index}`} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Activity / Organization Name *</Label>
                <Input value={form.name || ''} onChange={e => update('name', e.target.value)}
                  placeholder="e.g., Math Olympiad Team" className="border-[#E2E2E2]" data-testid={`activity-name-${index}`} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Participation Timing</Label>
                <Select value={form.timing || ''} onValueChange={v => update('timing', v)}>
                  <SelectTrigger data-testid={`activity-timing-${index}`} className="border-[#E2E2E2]"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{TIMING_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Grade Levels</Label>
              <div className="flex gap-2">
                {GRADE_OPTIONS.map(g => (
                  <button key={g} onClick={() => toggleGrade(g)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-all ${(form.grade_levels || []).includes(g) ? 'bg-[#0F392B] text-white border-[#0F392B]' : 'bg-white text-muted-foreground border-[#E2E2E2] hover:border-[#0F392B]/30'}`}
                    data-testid={`activity-grade-${index}-${g}`}>{g}th</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Hours / Week</Label>
                <Input type="number" min="0" max="168" value={form.hours_per_week || ''}
                  onChange={e => update('hours_per_week', e.target.value ? parseInt(e.target.value) : '')}
                  placeholder="e.g., 5" className="border-[#E2E2E2]" data-testid={`activity-hours-${index}`} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Weeks / Year</Label>
                <Input type="number" min="0" max="52" value={form.weeks_per_year || ''}
                  onChange={e => update('weeks_per_year', e.target.value ? parseInt(e.target.value) : '')}
                  placeholder="e.g., 40" className="border-[#E2E2E2]" data-testid={`activity-weeks-${index}`} />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer" data-testid={`activity-college-${index}`}>
                  <input type="checkbox" checked={form.continue_in_college || false}
                    onChange={e => update('continue_in_college', e.target.checked)}
                    className="rounded border-[#E2E2E2] text-[#0F392B] focus:ring-[#0F392B]" />
                  <span className="text-xs text-muted-foreground">Continue in college</span>
                </label>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Description (max 150 characters) *</Label>
                <span className={`text-xs font-mono ${descLen > DESC_LIMIT ? 'text-red-500 font-bold' : descLen > 120 ? 'text-amber-500' : 'text-muted-foreground'}`}>
                  {descLen}/{DESC_LIMIT}
                </span>
              </div>
              <Textarea value={form.description || ''} onChange={e => {
                if (e.target.value.length <= DESC_LIMIT) update('description', e.target.value);
              }} placeholder="Describe your activity, impact, and accomplishments..." rows={2}
                className="border-[#E2E2E2] resize-none text-sm" data-testid={`activity-desc-${index}`} />
              {form.description && (
                <Button size="sm" variant="outline" disabled={optimizing}
                  onClick={() => onOptimize(index, form)}
                  className="text-xs h-7 border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/5"
                  data-testid={`activity-optimize-${index}`}>
                  {optimizing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                  AI Optimize
                </Button>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-[#E2E2E2]">
              {activity?.id && (
                <Button size="sm" variant="ghost" onClick={() => onDelete(activity.id, activity.name)}
                  className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 h-8"
                  data-testid={`activity-delete-${index}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              )}
              <div className="ml-auto">
                <Button size="sm" onClick={handleSave} disabled={!form.name?.trim()}
                  className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-8"
                  data-testid={`activity-save-${index}`}>
                  {activity?.id ? 'Update' : 'Save Activity'}
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

export default function ActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optimizingIdx, setOptimizingIdx] = useState(null);
  const [batchOptimizing, setBatchOptimizing] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(() => {
    api.getActivities()
      .then(data => setActivities(data))
      .catch(() => toast.error('Failed to load activities'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = activities.findIndex(a => a.id === active.id);
    const newIndex = activities.findIndex(a => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(activities, oldIndex, newIndex);
    setActivities(newOrder);

    try {
      const order = newOrder.map((a, i) => ({ id: a.id, slot_number: i + 1 }));
      await api.reorderActivities(order);
      toast.success('Order updated');
    } catch (err) {
      toast.error('Reorder failed');
      load();
    }
  };

  const handleSave = async (index, formData) => {
    const existing = activities[index];
    try {
      const payload = {
        ...formData,
        hours_per_week: formData.hours_per_week ? parseInt(formData.hours_per_week) : null,
        weeks_per_year: formData.weeks_per_year ? parseInt(formData.weeks_per_year) : null,
      };
      if (existing?.id) {
        await api.updateActivity(existing.id, payload);
        toast.success('Activity updated');
      } else {
        await api.createActivity(payload);
        toast.success('Activity saved');
      }
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from your activities?`)) return;
    try { await api.deleteActivity(id); toast.success('Activity removed'); load(); } catch (err) { toast.error(err.message); }
  };

  const handleOptimize = async (index, form) => {
    setOptimizingIdx(index);
    try {
      const result = await api.optimizeActivity({
        description: form.description,
        activity_name: form.name,
        position: form.position_title || form.leadership_role || '',
      });
      if (result.optimized) {
        const existing = activities[index];
        if (existing?.id) await api.updateActivity(existing.id, { description: result.optimized });
        toast.success('Description optimized by AI');
        load();
      }
    } catch (err) { toast.error('Optimization failed'); }
    setOptimizingIdx(null);
  };

  const handleBatchOptimize = async () => {
    const toOptimize = activities.filter(a => a.description && a.id);
    if (toOptimize.length === 0) { toast.error('No activities with descriptions to optimize'); return; }

    setBatchOptimizing(true);
    try {
      const result = await api.batchOptimizeActivities(
        toOptimize.map(a => ({
          id: a.id,
          description: a.description,
          activity_name: a.name,
          position: a.position_title || a.leadership_role || '',
        }))
      );
      toast.success(`${result.optimized_count} activities optimized`);
      load();
    } catch (err) { toast.error('Batch optimization failed'); }
    setBatchOptimizing(false);
  };

  const completedCount = activities.filter(a => a.name && a.activity_type && a.description).length;
  const totalHours = activities.reduce((s, a) => s + ((a.hours_per_week || 0) * (a.weeks_per_year || 0)), 0);

  // Build slot array: existing activities + empty slots up to 10
  const slots = [];
  for (let i = 0; i < MAX_ACTIVITIES; i++) {
    slots.push(activities[i] || null);
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-3 animate-pulse">
        {[1,2,3,4,5].map(i => <div key={i} className="h-14 bg-[#E2E2E2] rounded-xl" />)}
      </div>
    );
  }

  // Only existing activities are sortable
  const sortableIds = activities.map(a => a.id);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="activities-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Activities
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            {activities.length}/{MAX_ACTIVITIES} slots used · {completedCount} complete · {totalHours.toLocaleString()} hrs/yr total
          </p>
        </div>
        {activities.filter(a => a.description).length > 0 && (
          <Button size="sm" onClick={handleBatchOptimize} disabled={batchOptimizing}
            className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white text-xs h-8"
            data-testid="batch-optimize-btn">
            {batchOptimizing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Zap className="h-3.5 w-3.5 mr-1" />}
            Optimize All
          </Button>
        )}
      </div>

      <div className="h-1.5 bg-[#F2F2F0] rounded-full overflow-hidden">
        <div className="h-full bg-[#0F392B] rounded-full transition-all duration-500"
          style={{ width: `${(activities.length / MAX_ACTIVITIES) * 100}%` }} />
      </div>

      <div className="flex items-start gap-3 bg-[#F9F9F7] rounded-lg p-4 border border-[#E2E2E2]" data-testid="activities-info">
        <Info className="h-4 w-4 text-[#0F392B] mt-0.5 flex-shrink-0" />
        <div className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Common App Format:</strong> Up to 10 activities, 150-char descriptions.
          Drag <GripVertical className="h-3 w-3 inline text-muted-foreground" /> to reorder by importance.
          Use <Zap className="h-3 w-3 inline text-[#D4AF37]" /> Optimize All to AI-enhance every description at once.
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {slots.map((activity, i) => (
              <SortableSlot
                key={activity?.id || `empty-${i}`}
                activity={activity}
                index={i}
                onSave={handleSave}
                onDelete={handleDelete}
                onOptimize={handleOptimize}
                optimizing={optimizingIdx === i}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <p className="text-xs text-muted-foreground text-center">
        Activities are ordered by importance. Drag to reorder. Admissions officers review them top to bottom.
      </p>
    </div>
  );
}
